<template>
  <div class="space-y-4 max-w-2xl">
    <div class="flex items-center gap-3">
      <button
        class="btn-secondary px-2"
        type="button"
        title="Back to vendors"
        @click="router.push({ name: 'VendorList' })"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <h1 class="text-xl font-bold text-gray-900">Vendor</h1>
    </div>

    <div v-if="error" class="card border-red-200 bg-red-50">
      <p class="text-sm font-semibold text-red-700 m-0">This vendor could not be found</p>
      <p class="text-xs text-red-700 mt-1 m-0">{{ error }}</p>
      <router-link :to="{ name: 'VendorList' }" class="btn-secondary mt-3 inline-flex">Back to vendors</router-link>
    </div>

    <div v-else-if="loading" class="card">
      <p class="text-sm text-gray-500 m-0">Loading vendor…</p>
    </div>

    <div v-else-if="vendor" class="space-y-4">
      <div v-if="archived" class="card border-amber-200 bg-amber-50 flex items-center justify-between gap-4">
        <div>
          <p class="text-sm font-semibold text-amber-900 m-0">This vendor is archived</p>
          <p class="text-xs text-amber-800 mt-1 m-0">
            {{ formatArchivedOn(vendor) }}. It's hidden from the board, pickers and imports, and its settings are read-only.
          </p>
        </div>
        <button class="btn-primary shrink-0" type="button" :disabled="archiving" @click="unarchive">
          {{ archiving ? 'Unarchiving…' : 'Unarchive' }}
        </button>
      </div>
      <div class="card space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="md:col-span-2">
            <span class="label">Name</span>
            <p class="text-sm text-gray-900 m-0">{{ vendor.name }}</p>
          </div>
          <div>
            <span class="label">Email</span>
            <p class="text-sm text-gray-900 m-0">{{ vendor.email ?? '—' }}</p>
          </div>
          <div>
            <span class="label">City</span>
            <p class="text-sm text-gray-900 m-0">{{ vendor.city ?? '—' }}</p>
          </div>
          <div>
            <span class="label">Country</span>
            <p class="text-sm text-gray-900 m-0">{{ vendor.country ?? '—' }}</p>
          </div>
          <div>
            <span class="label">Odoo status</span>
            <p class="text-sm text-gray-900 m-0">{{ vendor.isActive ? 'Active' : 'Inactive' }}</p>
          </div>
          <div class="md:col-span-2">
            <label class="label" for="alias">Alias</label>
            <input id="alias" v-model="alias" class="input" :readonly="archived" placeholder="An internal name to identify this vendor by" />
          </div>
        </div>

        <div class="flex gap-3 justify-end pt-2">
          <button v-if="isDirty && !archived" class="btn-primary" type="button" :disabled="saving" @click="save">
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>

      <div class="card space-y-3">
        <div>
          <h3 class="text-sm font-semibold text-gray-900 m-0">Shipping settings</h3>
          <p class="text-xs text-gray-500 mt-1 mb-0">
            Days from leaving the vendor to arriving at viaPhoton. New delivery requests use it to work out the ship-by date; open requests keep
            the ship-by they were sent with.
          </p>
        </div>
        <div class="flex flex-col gap-1">
          <label class="label flex w-fit flex-col gap-1" for="transit-days">
            Transit time (days)
            <input
              id="transit-days"
              v-model="transitDays"
              type="number"
              min="0"
              :max="MAX_TRANSIT_DAYS"
              class="input w-32 text-right"
              :class="transitError ? 'border-red-400' : ''"
              :readonly="archived"
              :aria-invalid="transitError !== null"
              :aria-describedby="transitError ? 'transit-days-error' : undefined"
              @keydown.enter.prevent="saveTransitDays"
            />
          </label>
          <span v-if="transitError" id="transit-days-error" class="text-xs text-red-700">{{ transitError }}</span>
        </div>
        <div v-if="transitDirty && !archived" class="flex justify-end pt-1">
          <button class="btn-primary" type="button" :disabled="savingTransit" @click="saveTransitDays">
            {{ savingTransit ? 'Saving…' : 'Save shipping settings' }}
          </button>
        </div>
      </div>

      <div class="card space-y-3">
        <div>
          <h3 class="text-sm font-semibold text-gray-900 m-0">Capacity settings</h3>
          <p class="text-xs text-gray-500 mt-1 mb-0">
            Which types this vendor produces, with the capacity factor applied when importing their capacity plans. Types are also added
            automatically when an imported plan mentions them. Unchecking a type hides its calendar data without deleting it — use “Clear data” to
            delete the data permanently.
          </p>
        </div>

        <p v-if="settingsError" class="text-xs text-red-700 m-0">{{ settingsError }}</p>
        <p v-else-if="settingsLoading" class="text-xs text-gray-500 m-0">Loading capacity settings…</p>
        <template v-else>
          <div v-for="type in capacityTypes" :key="type.id" class="flex min-h-8 items-center gap-4">
            <label class="flex w-28 shrink-0 items-center gap-2 text-sm text-gray-900">
              <input type="checkbox" :checked="type.code in draftSettings" :disabled="savingSettings || archived" @change="toggleType(type.code)" />
              {{ type.label }}
            </label>
            <!-- Fixed-width column: the pill's natural width varies by category and would
                 otherwise push the factor column out of alignment across rows. -->
            <span class="flex w-28 shrink-0">
              <CapacityTypePill :category="type.category" :label="type.category === 'molding' ? 'Molding' : 'Termination'" />
            </span>
            <label v-if="type.code in draftSettings" class="flex items-center gap-2 text-xs text-gray-500">
              Capacity factor
              <input
                :value="draftSettings[type.code] ?? ''"
                type="number"
                min="0.05"
                max="1"
                step="0.05"
                placeholder="—"
                class="input w-24 text-right"
                :disabled="savingSettings || archived"
                @change="onCapacityFactorChange(type.code, $event)"
              />
            </label>
            <span v-else class="text-xs text-gray-400">not offered</span>
            <button
              v-if="!archived"
              class="ml-auto text-xs text-red-600 hover:underline"
              type="button"
              title="Permanently delete all calendar (capacity) data for this type"
              :disabled="savingSettings || clearingData"
              @click="requestClearData(type.code)"
            >
              Clear data
            </button>
          </div>

          <div v-if="settingsDirty && !archived" class="flex justify-end pt-1">
            <button class="btn-primary" type="button" :disabled="savingSettings || !settingsValid" @click="saveSettings">
              {{ savingSettings ? 'Saving…' : 'Save capacity settings' }}
            </button>
          </div>
        </template>
      </div>

      <div v-if="!archived" class="card border-red-200 space-y-3">
        <div>
          <h3 class="text-sm font-semibold text-gray-900 m-0">Retire this vendor</h3>
          <p class="text-xs text-gray-500 mt-1 mb-0">
            Archiving hides the vendor from the board, pickers and imports and keeps its history; you can undo it. Delete only a vendor added by
            mistake — one with capacity plans or PO assignments can't be deleted.
          </p>
        </div>
        <div class="flex gap-3">
          <button class="btn-primary" type="button" :disabled="archiving" @click="requestArchive">Archive vendor</button>
          <button class="btn-danger" type="button" @click="confirmingDelete = true">Delete vendor</button>
        </div>
      </div>
      <div v-else class="card border-red-200 space-y-3">
        <div>
          <h3 class="text-sm font-semibold text-gray-900 m-0">Delete this vendor</h3>
          <p class="text-xs text-gray-500 mt-1 mb-0">Only a vendor with no capacity plans or PO assignments can be deleted.</p>
        </div>
        <button class="btn-danger" type="button" @click="confirmingDelete = true">Delete vendor</button>
      </div>
    </div>

    <ConfirmDialog
      :open="confirmingDelete"
      title="Delete vendor"
      :message="`Delete ${displayName}? This cannot be undone.`"
      :error="deleteError"
      confirm-label="Delete"
      @cancel="cancelDelete"
      @confirm="remove"
    />

    <ConfirmDialog
      :open="archiveMessage !== null"
      title="Archive vendor"
      :message="archiveMessage ?? ''"
      confirm-label="Archive"
      @cancel="archiveMessage = null"
      @confirm="confirmArchive"
    />

    <ConfirmDialog
      :open="clearDataCode !== null"
      title="Delete calendar data?"
      :message="clearDataConfirmMessage"
      confirm-label="Delete data"
      @cancel="cancelClearData"
      @confirm="confirmClearData"
    />

  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, reactive, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import CapacityTypePill from '@/components/capacity/CapacityTypePill.vue';
  import logger from '@/services/logger';
  import { fetchCapacityTypes } from '@/services/capacityTypeService';
  import {
    deleteCapacityCalendarData,
    fetchArchiveImpact,
    fetchCapacityRemovalImpact,
    fetchVendorById,
    fetchVendorCapacitySettings,
    saveVendorCapacitySettings,
    setTransitDays,
  } from '@/services/vendorService';
  import { useVendorStore } from '@/store/vendorStore';
  import type { CapacityType } from '@/types/capacityType';
  import type { CapacityRemovalImpact, Vendor } from '@/types/vendor';
  import { extractErrorMessage } from '@/utils/apiError';
  import { parseNullableNumber } from '@/utils/numberInput';
  import { buildArchiveConfirmMessage, formatArchivedOn, isArchived, vendorDisplayName } from '@/utils/vendorArchive';
  import { buildRemovalImpactMessage } from '@/utils/vendorCapacitySettings';
  import { MAX_TRANSIT_DAYS, validateTransitDays } from '@/utils/vendorTransit';

  const route = useRoute();
  const router = useRouter();
  const vendorStore = useVendorStore();

  const id = String(route.params.id);
  const vendor = ref<Vendor | null>(null);
  const alias = ref('');
  const originalAlias = ref('');
  const loading = ref(true);
  const saving = ref(false);
  const error = ref<string | null>(null);
  const confirmingDelete = ref(false);

  const archived = computed(() => vendor.value !== null && isArchived(vendor.value));
  const displayName = computed(() => (vendor.value ? vendorDisplayName(vendor.value) : ''));

  const archiveMessage = ref<string | null>(null);
  const archiving = ref(false);
  const deleteError = ref<string | null>(null);

  const isDirty = computed(() => alias.value.trim() !== originalAlias.value);

  // A number input's v-model yields a number once typed; kept as text for the validator.
  const transitDays = ref<string | number>('');
  const transitError = ref<string | null>(null);
  const savingTransit = ref(false);
  const transitDirty = computed(() => vendor.value !== null && String(transitDays.value) !== String(vendor.value.transitDays));

  const FULL_CAPACITY_FACTOR = 1;
  const capacityTypes = ref<CapacityType[]>([]);
  // A checked type with capacityFactor null means "produced, but the factor isn't known yet"
  // (e.g. auto-registered by an import whose sheet left that column blank) — still savable.
  const draftSettings = reactive<Record<string, number | null>>({});
  const originalSettings = ref<Record<string, number | null>>({});
  const settingsLoading = ref(true);
  const savingSettings = ref(false);
  const settingsError = ref<string | null>(null);
  const settingsDirty = computed(() => JSON.stringify(draftSettings) !== JSON.stringify(originalSettings.value));
  const settingsValid = computed(() =>
    Object.values(draftSettings).every((factor) => factor === null || (Number.isFinite(factor) && factor > 0 && factor <= FULL_CAPACITY_FACTOR)),
  );
  const clearDataCode = ref<string | null>(null);
  const clearDataImpact = ref<CapacityRemovalImpact[]>([]);
  const clearingData = ref(false);
  const clearDataConfirmMessage = computed(() => buildRemovalImpactMessage(clearDataImpact.value));

  function toggleType(code: string): void {
    if (code in draftSettings) {
      delete draftSettings[code];
      return;
    }
    draftSettings[code] = originalSettings.value[code] ?? FULL_CAPACITY_FACTOR;
  }

  function onCapacityFactorChange(code: string, event: Event): void {
    draftSettings[code] = parseNullableNumber((event.target as HTMLInputElement).value);
  }

  async function loadSettings(): Promise<void> {
    try {
      const [types, settings] = await Promise.all([fetchCapacityTypes(), fetchVendorCapacitySettings(id)]);
      capacityTypes.value = types;
      settings.forEach((setting) => {
        draftSettings[setting.capacityTypeCode] = setting.capacityFactor;
      });
      originalSettings.value = { ...draftSettings };
    } catch (loadError) {
      settingsError.value = extractErrorMessage(loadError, 'Failed to load capacity settings');
    } finally {
      settingsLoading.value = false;
    }
  }

  // Saving never deletes calendar data — removed types are only hidden from the calendar,
  // so no impact check or confirmation is needed here (deletion lives in the clear-data flow).
  async function saveSettings(): Promise<void> {
    savingSettings.value = true;
    try {
      const settings = Object.entries(draftSettings).map(([capacityTypeCode, capacityFactor]) => ({ capacityTypeCode, capacityFactor }));
      await saveVendorCapacitySettings(id, settings);
      originalSettings.value = { ...draftSettings };
      logger.showSuccess('Capacity settings saved');
    } catch (saveError) {
      logger.showError(extractErrorMessage(saveError));
    } finally {
      savingSettings.value = false;
    }
  }

  async function requestClearData(code: string): Promise<void> {
    clearingData.value = true;
    try {
      const impact = await fetchCapacityRemovalImpact(id, [code]);
      if (impact.length === 0) {
        logger.showInfo(`No calendar data to delete for ${labelFor(code)}`);
        return;
      }
      clearDataImpact.value = impact;
      clearDataCode.value = code;
    } catch (impactError) {
      logger.showError(extractErrorMessage(impactError, 'Failed to check calendar data impact'));
    } finally {
      clearingData.value = false;
    }
  }

  async function confirmClearData(): Promise<void> {
    const code = clearDataCode.value;
    clearDataCode.value = null;
    clearDataImpact.value = [];
    if (!code) return;

    clearingData.value = true;
    try {
      await deleteCapacityCalendarData(id, [code]);
      logger.showSuccess(`Calendar data deleted for ${labelFor(code)}`);
    } catch (deleteError) {
      logger.showError(extractErrorMessage(deleteError));
    } finally {
      clearingData.value = false;
    }
  }

  function cancelClearData(): void {
    clearDataCode.value = null;
    clearDataImpact.value = [];
  }

  function labelFor(code: string): string {
    return capacityTypes.value.find((type) => type.code === code)?.label ?? code;
  }

  function applyVendor(v: Vendor) {
    vendor.value = v;
    alias.value = v.alias ?? '';
    originalAlias.value = v.alias ?? '';
    transitDays.value = String(v.transitDays);
  }

  async function saveTransitDays(): Promise<void> {
    if (savingTransit.value || archived.value || !transitDirty.value) return;
    const text = String(transitDays.value);
    transitError.value = validateTransitDays(text)?.message ?? null;
    if (transitError.value) return;
    savingTransit.value = true;
    try {
      applyVendor(vendorStore.replaceVendor(await setTransitDays(id, Number(text))));
      logger.showSuccess('Shipping settings saved');
    } catch (saveError) {
      transitError.value = extractErrorMessage(saveError);
    } finally {
      savingTransit.value = false;
    }
  }

  onMounted(async () => {
    const fromStore = vendorStore.vendors.find((v) => v.id === id);
    if (fromStore) {
      applyVendor(fromStore);
      loading.value = false;
      return;
    }

    try {
      applyVendor(await fetchVendorById(id));
    } catch (loadError) {
      error.value = extractErrorMessage(loadError);
    } finally {
      loading.value = false;
    }
  });

  onMounted(loadSettings);

  async function save() {
    saving.value = true;
    try {
      const updated = await vendorStore.updateVendorAlias(id, alias.value.trim() || null);
      applyVendor(updated);
      logger.showSuccess(`${updated.alias || updated.name} saved`);
    } catch (saveError) {
      logger.showError(extractErrorMessage(saveError));
    } finally {
      saving.value = false;
    }
  }

  async function requestArchive(): Promise<void> {
    archiving.value = true;
    try {
      archiveMessage.value = buildArchiveConfirmMessage(displayName.value, await fetchArchiveImpact(id));
    } catch (impactError) {
      logger.warn(`Archive impact check failed: ${extractErrorMessage(impactError)}`);
      archiveMessage.value = buildArchiveConfirmMessage(displayName.value, null);
    } finally {
      archiving.value = false;
    }
  }

  async function confirmArchive(): Promise<void> {
    archiveMessage.value = null;
    archiving.value = true;
    try {
      applyVendor(await vendorStore.archiveVendor(id));
      logger.showSuccess(`${displayName.value} archived`);
    } catch (archiveError) {
      logger.showError(extractErrorMessage(archiveError));
    } finally {
      archiving.value = false;
    }
  }

  async function unarchive(): Promise<void> {
    archiving.value = true;
    try {
      applyVendor(await vendorStore.unarchiveVendor(id));
      logger.showSuccess(`${displayName.value} unarchived`);
    } catch (unarchiveError) {
      logger.showError(extractErrorMessage(unarchiveError));
    } finally {
      archiving.value = false;
    }
  }

  async function remove() {
    deleteError.value = null;
    try {
      await vendorStore.removeVendor(id);
      confirmingDelete.value = false;
      logger.showSuccess(`${displayName.value} deleted`);
      await router.push({ name: 'VendorList' });
    } catch (removeError) {
      deleteError.value = extractErrorMessage(removeError);
    }
  }

  function cancelDelete(): void {
    confirmingDelete.value = false;
    deleteError.value = null;
  }
</script>
